import { useEffect, useMemo, useRef } from "react";
import type { SituationDisposition, SituationSummary, Stage } from "@mosaic/shared";
import { RADAR_STAGES, STAGE_LABEL, stageColorVar } from "../lib/format.js";
import { SituationRow } from "./SituationRow.js";
import { SearchIcon } from "./icons.js";

/**
 * The radar's filter: a text query, the stages, whether archived situations are shown, and
 * which dispositions.
 */
export interface RadarFilter {
  q: string;
  stage: Stage[];
  archived: boolean;
  /** Empty means the radar's default: both dismissals hidden. */
  disposition: SituationDisposition[];
}

interface Props {
  situations: SituationSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  filter: RadarFilter;
  onFilter: (next: RadarFilter) => void;
  lastUpdated: number | null;
}

interface RadarView {
  id: string;
  label: string;
  /** Empty is the radar's own default: both dismissals hidden, archived hidden. */
  disposition: SituationDisposition[];
  archived: boolean;
  /**
   * What the header is counting, since a view replaces the list rather than adding to it.
   */
  countLabel: string;
  emptyTitle: string;
  emptyHint: string;
}

const ALL: RadarView = {
  id: "all",
  label: "All",
  disposition: [],
  archived: false,
  countLabel: "live",
  emptyTitle: "No situations yet.",
  emptyHint: "Run a scan from Operations to walk a trading day's index.",
};

/**
 * The chips filter by the owner's own judgement, and by nothing else.
 *
 * The radar already groups by stage and orders by time to distribution, so a stage chip
 * would repeat what the ordering shows. These filter on the one thing it cannot.
 *
 * **`Needs review` is the triage question**: `disposition = unreviewed`, the value every
 * situation opens at.
 *
 * **A dismissal chip is the way back, and it has to be complete.** Naming a disposition
 * lifts the archived default server-side (`buildWhere` in `situationQueries.ts`), so an
 * archived row surfaces under the judgement chip that matches it rather than an empty
 * list reporting nothing to undo.
 *
 * **The two dismissals are two chips, because they are two different findings.** `Not a
 * separation` says the screen was wrong and stops reconciliation matching new filings to
 * that row; `Dismissed` says the screen was right and the owner passed. Each names exactly
 * what its button does.
 */
const VIEWS: RadarView[] = [
  ALL,
  {
    id: "unreviewed",
    label: "Needs review",
    disposition: ["unreviewed"],
    archived: false,
    countLabel: "needing review",
    emptyTitle: "Nothing waiting on you.",
    emptyHint:
      "Every situation on the radar has a judgement — watching, dismissed, or not a separation.",
  },
  {
    id: "watching",
    label: "Watching",
    disposition: ["watching"],
    archived: false,
    countLabel: "watching",
    emptyTitle: "Nothing marked watching.",
    emptyHint: "Mark a situation as watching from its own page and it lands here.",
  },
  {
    id: "dismissed",
    label: "Dismissed",
    disposition: ["not_interesting"],
    archived: false,
    countLabel: "dismissed",
    emptyTitle: "Nothing dismissed.",
    emptyHint: "Real separations you have passed on live here.",
  },
  {
    id: "not_a_situation",
    label: "Not a separation",
    disposition: ["not_a_situation"],
    archived: false,
    countLabel: "not a separation",
    emptyTitle: "Nothing marked not a separation.",
    emptyHint:
      "Rows the screen opened that describe no separation live here — including " +
      "everything archived before dispositions existed.",
  },
];

/**
 * The chip matching a filter. Total, because these chips are the only writers of the two
 * axes it reads: every combination that reaches the column is one of them, and the bare
 * radar is `All`.
 *
 * @internal
 */
function activeView(filter: RadarFilter): RadarView {
  return (
    VIEWS.find(
      (v) =>
        v.archived === filter.archived &&
        v.disposition.length === filter.disposition.length &&
        v.disposition.every((d) => filter.disposition.includes(d)),
    ) ?? ALL
  );
}

/**
 * The radar's index column: a filter box focused by `/`, the view chips, and the matching
 * situations grouped by stage.
 *
 * @param total The number of situations the current view matches, for its count.
 * @param lastUpdated When the list was last fetched, in epoch milliseconds, or null.
 */
export function IndexColumn({
  situations,
  total,
  loading,
  error,
  filter,
  onFilter,
  lastUpdated,
}: Props) {
  const search = useRef<HTMLInputElement>(null);

  // `/` focuses the filter, as the mockup's kbd hint promises. Ignored while the
  // caret is already in a field, or typing a path into a note would steal focus.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey) return;
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      if (target?.isContentEditable) return;
      event.preventDefault();
      search.current?.focus();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const grouped = useMemo(() => {
    const byStage = new Map<Stage, SituationSummary[]>();
    for (const s of situations) {
      const list = byStage.get(s.stage) ?? [];
      list.push(s);
      byStage.set(s.stage, list);
    }
    // Pinned rows come back first from the API and would otherwise be scattered
    // across stage groups; they keep their group, and their chip marks them.
    return RADAR_STAGES.map((stage) => ({ stage, items: byStage.get(stage) ?? [] })).filter(
      (g) => g.items.length > 0,
    );
  }, [situations]);

  return (
    <div className="index">
      <div className="ihead">
        <div className="ititle">
          <h2>Radar</h2>
          <span className="ct">
            {/*
              Each view replaces the list rather than adding to it, so the count names
              the view.
            */}
            {loading && situations.length === 0
              ? "…"
              : `${total} ${activeView(filter).countLabel}`}
            {lastUpdated ? ` · ${agoLabel(lastUpdated)}` : ""}
          </span>
        </div>
        <div className="isearch">
          <SearchIcon />
          <input
            ref={search}
            value={filter.q}
            onChange={(e) => onFilter({ ...filter, q: e.target.value })}
            placeholder="Filter situations…"
            aria-label="Filter situations"
            spellCheck={false}
          />
          <span className="kbd">/</span>
        </div>
      </div>

      <div className="ifilters">
        {VIEWS.map((view) => {
          const on = activeView(filter).id === view.id;
          return (
            <button
              key={view.id}
              className={`chip${on ? " on" : ""}`}
              aria-pressed={on}
              onClick={() =>
                onFilter({ ...filter, archived: view.archived, disposition: view.disposition })
              }
            >
              {view.label}
            </button>
          );
        })}
      </div>

      <div className="ilist">
        {error && (
          <div className="empty">
            <p>{error}</p>
          </div>
        )}
        {!error && situations.length === 0 && !loading && (
          <div className="empty">
            <p>{emptyTitle(filter)}</p>
            <p className="hint">{emptyHint(filter)}</p>
          </div>
        )}
        {grouped.map(({ stage, items }) => (
          <section key={stage}>
            <header className="grouphd" style={{ ["--sc" as string]: stageColorVar(stage) }}>
              <span>
                <i />
                {STAGE_LABEL[stage]}
              </span>
              <span>{items.length}</span>
            </header>
            {items.map((s) => (
              <SituationRow key={s.id} situation={s} />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * The heading shown when the list is empty under this filter.
 *
 * @internal
 * @param filter The filter in force.
 * @returns A sentence for a text query that matched nothing, else the view's own heading.
 */
function emptyTitle(filter: RadarFilter): string {
  if (filter.q) return "Nothing matches that filter.";
  // An empty view is not an empty corpus, and telling the owner to run a scan would be
  // advice for a state they are not in.
  return activeView(filter).emptyTitle;
}

/**
 * The hint shown beneath the empty-list heading.
 *
 * @internal
 * @param filter The filter in force.
 * @returns A suggestion for a text query, else the view's own hint.
 */
function emptyHint(filter: RadarFilter): string {
  if (filter.q) return "Try a ticker, a company name, or clear the filter.";
  return activeView(filter).emptyHint;
}

/**
 * How long ago a moment was, as `just now`, minutes or hours.
 *
 * @internal
 * @param at The moment, in epoch milliseconds.
 * @returns A short relative label, such as `5m ago`.
 */
function agoLabel(at: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}
